import { z } from 'zod';
import { ConnectorKind } from './kind.js';
/**
 * Per-install secrets — the credentials that VARY per customer install.
 * Stored in `orgConnectorSecrets/{id}` (a separate collection from
 * `orgConnectors/{id}` so a hostile/curious orgAdmin can't read tokens
 * via the Firestore SDK even though they can read the public connector
 * state).
 *
 * Platform-level secrets (Slack signingSecret, GitHub appPrivateKey /
 * webhookSecret, Google OAuth client secret, Salesforce client secret)
 * are NOT here — those are one-per-vendor-app values bound at the
 * function level via `defineSecret`. Reviewers flagged the original
 * design as a blast-radius issue: per-org docs duplicating app-level
 * secrets across every customer means a single doc read leaks the
 * platform-wide signing key.
 *
 * Stripe is the exception — it stays BYO (admin pastes per-org
 * restricted key + webhook secret), so both fields live on the
 * per-install record.
 */
const SlackSecrets = z.object({
    accessToken: z.string().min(1),
    refreshToken: z.string().optional(),
    /**
     * Unix-seconds expiry for accessToken when Slack's rotating-tokens
     * feature is enabled on the app. Optional because non-rotating Slack
     * workspace tokens don't expire — only present when the OAuth
     * response carried `expires_in`. When set, the token-refresh helper
     * proactively refreshes before this timestamp; when unset, refresh
     * only fires on a 401 from Slack.
     */
    expiresAt: z.number().int().positive().optional(),
    /** Slack workspace team id; identifies which workspace the token belongs to. */
    teamId: z.string().min(1),
    /** Bot user id for the installation. */
    botUserId: z.string().optional(),
});
const GmailSecrets = z.object({
    /** Per-member uid this token belongs to (Gmail is per-user). */
    memberUid: z.string().min(1),
    accessToken: z.string().min(1),
    refreshToken: z.string().min(1),
    /** Unix-seconds expiry for accessToken; refreshed via refreshToken when past. */
    expiresAt: z.number().int().positive(),
    /**
     * The Google account email this install belongs to (resolved from the
     * Gmail profile endpoint at completeInstall). The webhook orchestrator
     * uses this to route Pub/Sub pushes to the right org — without it,
     * the resolver can't disambiguate which tenant owns the inbox.
     * Stored lowercase for case-insensitive match.
     */
    connectedEmail: z.string().email(),
    /** Pub/Sub topic the historyId watch is bound to. */
    historyId: z.string().optional(),
});
const SalesforceSecrets = z.object({
    accessToken: z.string().min(1),
    refreshToken: z.string().min(1),
    /** my.salesforce.com instance host (varies per org). */
    instanceUrl: z.string().url(),
    /** The CDC channel + sObjects the admin opted into. */
    channel: z.string().optional(),
    sObjects: z.array(z.string().min(1)).default([]),
});
const GithubSecrets = z.object({
    /** GitHub App installation id (one per GH org). */
    installationId: z.string().min(1),
});
const StripeSecrets = z.object({
    /** Restricted Stripe API key (rk_...). */
    apiKey: z.string().min(4),
    /** Webhook signing secret (whsec_...) for Stripe-Signature verification. */
    webhookSecret: z.string().min(4),
});
export const ConnectorSecrets = z.discriminatedUnion('kind', [
    SlackSecrets.extend({ kind: z.literal('slack') }),
    GmailSecrets.extend({ kind: z.literal('gmail') }),
    SalesforceSecrets.extend({ kind: z.literal('salesforce') }),
    GithubSecrets.extend({ kind: z.literal('github') }),
    StripeSecrets.extend({ kind: z.literal('stripe') }),
]);
/**
 * Body shape for `POST /api/orgs/:o/connectors/:kind/install` —
 * starts the OAuth dance. The server returns the vendor's auth URL
 * the admin (or member, for Gmail) follows.
 */
export const StartInstallRequest = z.object({
    /** When the install scope is `per-user`, this can pin the binding. */
    memberUid: z.string().optional(),
});
/** Response body for /install — the vendor auth URL the UI redirects to. */
export const StartInstallResponse = z.object({
    kind: ConnectorKind,
    /** The auth URL the user opens to grant scopes. */
    authUrl: z.string().url(),
    /** State token the callback receiver expects back. */
    state: z.string().min(1),
});
